import {DOCUMENT} from '@angular/common';
import {Component, EventEmitter, HostBinding, Inject, Input, OnInit, Output, QueryList, Renderer2, ViewChild, ViewChildren} from '@angular/core';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {IPageInfo, VirtualScrollerComponent} from '@iharbeck/ngx-virtual-scroller';
import {BaseComponent, EllipsisLabelComponent} from '@vdw/angular-component-library';
import {cloneDeep, findIndex, first, isEmpty, isEqual, isNil, last, remove, size} from 'lodash-es';
import {Subject} from 'rxjs';
import {buffer, debounceTime, takeUntil} from 'rxjs/operators';
import {ListDrawing} from './list-drawing';

@Component({
  selector: 'app-drawing-list',
  templateUrl: './drawing-list.component.html',
  styleUrls: ['./drawing-list.component.scss']
})
export class DrawingListComponent extends BaseComponent implements OnInit {
  public static MISSING_DATA_IMAGE = 'assets/images/missing-design.svg';

  @ViewChildren('ellipsisLabel') public ellipsisLabels: QueryList<EllipsisLabelComponent>;
  @ViewChild('virtualScroller') public virtualScroller: VirtualScrollerComponent;

  @Input() public selectedDrawings: ListDrawing[] = [];
  @Input() public listOfDrawings: ListDrawing[] = [];
  @Input() public allowSelectMultipleDrawings = false;
  @Input() public allowDoubleClick = false;
  @Input() public filteredSelectedDrawings: ListDrawing[] = [];
  @Input() public canDragDrawings = false;
  @Input() public showSelectedOverlay = true;
  @Input() public productionScheduleItemMultiplier: number;

  @HostBinding('class.can-select-drawings') @Input() public canSelectDrawings = true;
  @HostBinding('class.editing-paths') @Input() public isEditingPaths = false;

  @Output() public drawingSelected: EventEmitter<ListDrawing[]> = new EventEmitter<ListDrawing[]>();
  @Output() public drawingDoubleClicked: EventEmitter<ListDrawing> = new EventEmitter<ListDrawing>();
  @Output() public virtualScrollerEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() public drawingDragStart: EventEmitter<ListDrawing> = new EventEmitter<ListDrawing>();
  @Output() public drawingDragEnd: EventEmitter<void> = new EventEmitter<void>();

  private drawingDragPreview: HTMLElement = null;

  private readonly GAP_BETWEEN_PREVIEW_AND_NAME_IN_PX = 8;
  private readonly drawingClickSubject: Subject<ListDrawing> = new Subject<ListDrawing>();

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this.allowDoubleClick) {
      this.subscribeToDrawingClickSubject();
    }
  }

  public onClickDrawing(listDrawing: ListDrawing, event: MouseEvent): void {
    if (this.canSelectDrawings) {
      if (this.allowDoubleClick) {
        this.drawingClickSubject.next(listDrawing);
      } else {
        this.handleDrawingSingleClick(listDrawing, event);
      }
    }
  }

  public canShowSelectedIndicator(listDrawing: ListDrawing): boolean {
    return this.showSelectedOverlay && this.isDrawingSelected(listDrawing);
  }

  public onVirtualScrollerEndEvent(pageInfo: IPageInfo): void {
    if (isEqual(pageInfo.endIndex, size(this.listOfDrawings) - 1)) {
      this.virtualScrollerEnd.emit();
    }
  }

  public hasDrawingImageData(drawingImage: DrawingImage): boolean {
    return !isNil(drawingImage.data);
  }

  public imageDownloaded(drawing: Drawing, imageData: string): void {
    const configurationFromDrawing = drawing.configurations[0] as DrawingConfigurationWithColorAndYarnSet;

    const imageIndexInListOfFilteredDrawings: number = this.listOfDrawings.findIndex((listDrawing: ListDrawing) => {
      const configurationFromListDrawing = listDrawing.drawing.configurations[0] as DrawingConfigurationWithColorAndYarnSet;
      return (
        listDrawing.drawing.image?.id === drawing.image?.id &&
        configurationFromListDrawing?.quality?.id === configurationFromDrawing?.quality?.id &&
        configurationFromListDrawing?.colorSet?.id === configurationFromDrawing?.colorSet?.id
      );
    });

    const filteredDrawing: ListDrawing = this.listOfDrawings[imageIndexInListOfFilteredDrawings];
    if (!isNil(filteredDrawing)) {
      filteredDrawing.drawing.image.data = imageData;
    }

    this.listOfDrawings[imageIndexInListOfFilteredDrawings] = filteredDrawing;
  }

  public isDrawingTypeBmp(drawing: Drawing): boolean {
    return isEqual(drawing.drawingType, DrawingType.BMP);
  }

  public trackById = (index: number, listDrawing: ListDrawing): string => listDrawing.id;

  public onDrawingDragStart(event: any, listDrawing: ListDrawing): void {
    const dummyPreview: HTMLElement = this.renderer.createElement('div');

    this.drawingDragPreview = this.renderer.createElement('img');
    this.renderer.setAttribute(this.drawingDragPreview, 'src', listDrawing.drawing.missingDesign ? DrawingListComponent.MISSING_DATA_IMAGE : listDrawing.drawing.image.data);
    if (listDrawing.drawing.missingDesign) {
      this.renderer.setStyle(this.drawingDragPreview, 'background-color', 'white');
    }
    this.renderer.addClass(this.drawingDragPreview, 'drawing-drag-preview');
    this.renderer.setStyle(this.drawingDragPreview, 'transform', `translate(${event.clientX}px,${event.clientY}px)`);
    this.renderer.appendChild(this.document.body, this.drawingDragPreview);
    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');

    event.dataTransfer.setDragImage(dummyPreview, 0, 0);

    const draggedListDrawing = cloneDeep(listDrawing);

    this.drawingDragStart.emit(draggedListDrawing);
  }

  public onDrawingDrag(event: any): void {
    if (!isNil(this.drawingDragPreview) && !isEqual(event.screenX, 0) && !isEqual(event.screenY, 0)) {
      this.renderer.setStyle(this.drawingDragPreview, 'transform', `translate(${event.clientX}px,${event.clientY}px)`);
    }
  }

  public onDrawingDragEnd(event: any): void {
    const body: HTMLElement = this.document.querySelector('body');
    this.renderer.removeStyle(body, 'overflow');
    this.renderer.removeChild(body, this.drawingDragPreview);
    this.drawingDragPreview = null;
    event.dataTransfer.clearData();

    this.drawingDragEnd.emit();
  }

  public isDraggable(): boolean {
    return this.canDragDrawings && !this.isEditingPaths;
  }

  public canShowAmountSpecifier(): boolean {
    return this.canDragDrawings && !this.isEditingPaths;
  }

  public onMouseMove(event: MouseEvent, listDrawing: ListDrawing): void {
    if (this.canShowAmountSpecifier()) {
      const drawingContainerElement = Array.from((this.virtualScroller['element'].nativeElement as HTMLElement).children[1].children).find((element: Element) =>
        element.textContent.includes(listDrawing.drawing.name)
      );

      const amountSpecifierElement = drawingContainerElement.querySelector('app-amount-specifier');
      const drawingPreviewElement = drawingContainerElement.querySelector('.drawing');
      const drawingNameElement = drawingContainerElement.querySelector('.drawing-name');

      const drawingContainerTop = drawingContainerElement.getBoundingClientRect().top;
      const amountSpecifierTop = amountSpecifierElement.children[0].children[0].getBoundingClientRect().top;
      const amountSpecifierHeight = amountSpecifierElement.children[0].clientHeight;
      const drawingPreviewHeight = (drawingPreviewElement as HTMLElement).offsetHeight;
      const drawingNameHeight = (drawingNameElement as HTMLElement).offsetHeight;
      const clientY = event.clientY - drawingContainerTop;

      const isCursorNearAmountSpecifier =
        amountSpecifierTop - drawingContainerTop + amountSpecifierHeight < clientY &&
        clientY < amountSpecifierTop + amountSpecifierHeight - drawingContainerTop + this.GAP_BETWEEN_PREVIEW_AND_NAME_IN_PX / 2;
      const isCursorBelowName = clientY > drawingPreviewHeight + drawingNameHeight;
      const isCursorOnDrawingName = clientY > amountSpecifierTop + amountSpecifierHeight - drawingContainerTop + this.GAP_BETWEEN_PREVIEW_AND_NAME_IN_PX / 2;

      const ellipsisLabel = this.ellipsisLabels.find((component: EllipsisLabelComponent) => component.text === listDrawing.drawing.name);

      if (isCursorNearAmountSpecifier || isCursorBelowName) {
        ellipsisLabel.ellipsisTooltip.hide();
      } else if (isCursorOnDrawingName) {
        ellipsisLabel.ellipsisTooltip.show();
      }
    }
  }

  private handleDrawingSingleClick(listDrawing: ListDrawing, event: MouseEvent): void {
    let selectedDrawings = cloneDeep(this.selectedDrawings);

    if (this.allowSelectMultipleDrawings) {
      this.selectMultiple(listDrawing, event, selectedDrawings);
    } else {
      if (!isEmpty(selectedDrawings) && this.isDrawingSelected(listDrawing)) {
        remove(selectedDrawings, ['drawing.id', listDrawing.drawing.id]);
      } else {
        selectedDrawings = [listDrawing];
      }
    }

    this.drawingSelected.emit(selectedDrawings);
  }

  private subscribeToDrawingClickSubject(): void {
    this.drawingClickSubject.pipe(buffer(this.drawingClickSubject.pipe(debounceTime(250))), takeUntil(this.unSubscribeOnViewDestroy)).subscribe((listDrawings: ListDrawing[]) => {
      const listDrawing: ListDrawing = last(listDrawings);
      if (!this.allowSelectMultipleDrawings && isEqual(size(listDrawings), 2) && isEqual(first(listDrawings).drawing.id, listDrawing.drawing.id)) {
        this.drawingDoubleClicked.emit(listDrawing);
      } else {
        this.handleDrawingSingleClick(listDrawing, null);
      }
    });
  }

  private selectMultiple(listDrawing: ListDrawing, event: MouseEvent, selectedDrawings: ListDrawing[]): void {
    if (event.shiftKey || (event.metaKey && event.ctrlKey)) {
      this.selectMultipleWithKeyboardShortcuts(listDrawing, selectedDrawings);
    } else {
      if (!this.isDrawingSelected(listDrawing)) {
        selectedDrawings.push(listDrawing);
      } else {
        remove(selectedDrawings, ['drawing.id', listDrawing.drawing.id]);
      }
    }
  }

  private selectMultipleWithKeyboardShortcuts(listDrawing: ListDrawing, selectedDrawings: ListDrawing[]): void {
    const fromIndex = findIndex(this.listOfDrawings, last(selectedDrawings));
    const toIndex = findIndex(this.listOfDrawings, listDrawing);
    if (fromIndex < toIndex) {
      for (let i = fromIndex + 1; i <= toIndex; i++) {
        this.addDrawingToSelectedDrawingList(i, selectedDrawings);
      }
    } else {
      for (let i = fromIndex; i >= toIndex; i--) {
        this.addDrawingToSelectedDrawingList(i, selectedDrawings);
      }
    }
  }

  private addDrawingToSelectedDrawingList(index: number, selectedDrawings: ListDrawing[]): void {
    if (!this.isDrawingSelected(this.listOfDrawings[index])) {
      selectedDrawings.push(this.listOfDrawings[index]);
    }
  }

  private isDrawingSelected(listDrawing: ListDrawing): boolean {
    return this.selectedDrawings.some((selectedDrawing: ListDrawing) => selectedDrawing.drawing.id === listDrawing.drawing.id);
  }
}
